import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import type { Achievement, BuildStat, PerformanceRecord } from '../../types/buildExpansion';
import { BILT_MARK_MONO_SRC } from '../../utils/biltBrandDisplay';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  loadImageSafe,
  renderShareCard,
  type ShareCardFormat,
  type ShareCardHighlight,
} from '../../utils/shareCardCanvas';

export interface ShareCardVehicleInfo {
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  nickname?: string | null;
}

export interface ShareCardAward {
  id: number;
  title: string;
  placement?: string | null;
  event_title?: string | null;
}

export interface ShareCardGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: ShareCardVehicleInfo;
  /** Full public share URL for the vehicle. */
  shareUrl: string;
  vehicleId?: number;
  ownerName?: string | null;
  /** Public vehicle hero image URL; card degrades to typographic if it fails. */
  imageUrl?: string | null;
  /** Existing QR image URL for this vehicle (optional footer QR). */
  qrImageUrl?: string | null;
  stats?: BuildStat[];
  achievements?: Achievement[];
  /** Dyno and personal-best records used for highlight options. */
  records?: PerformanceRecord[];
  awards?: ShareCardAward[];
  /** Total modification spend; shown only when the invested toggle is on. */
  investedAmount?: number | null;
}

interface FormatChoice {
  value: ShareCardFormat;
  label: string;
  caption: string;
  previewClass: string;
}

const FORMAT_CHOICES: FormatChoice[] = [
  { value: 'story', label: 'Story', caption: '9:16', previewClass: 'w-5 h-9' },
  { value: 'portrait', label: 'Post', caption: '4:5', previewClass: 'w-7 h-9' },
  { value: 'square', label: 'Square', caption: '1:1', previewClass: 'w-8 h-8' },
];

/** Default stats on the card. "Documented" score/years are never offered. */
const DEFAULT_STAT_KEYS = ['power', 'torque', 'modification_count'] as const;
const EXCLUDED_STAT_KEYS = new Set(['documentation_score', 'years_documented']);
const MAX_FEATURED = 3;
const MAX_OPTIONS_PER_KIND = 6;

interface FeatureOption {
  key: string;
  kind: ShareCardHighlight['kind'];
  label: string;
  caption: string | null;
  highlight: ShareCardHighlight;
  verified: boolean;
}

function buildFeatureOptions(
  achievements: Achievement[],
  awards: ShareCardAward[],
  records: PerformanceRecord[]
): FeatureOption[] {
  const options: FeatureOption[] = [];

  achievements
    .filter((a) => Boolean(a.name) && (a.status === 'current' || a.status === null))
    .slice(0, MAX_OPTIONS_PER_KIND)
    .forEach((a) => {
      const detail =
        a.rank != null
          ? `Ranked #${a.rank}${a.population != null ? ` of ${a.population}` : ''}`
          : a.short_name && a.short_name !== a.name
            ? a.short_name
            : null;
      options.push({
        key: `achievement-${a.id}`,
        kind: 'achievement',
        label: a.name as string,
        caption: detail,
        highlight: { kind: 'achievement', title: a.name as string, detail },
        verified: a.achievement_class === 'verified_credential',
      });
    });

  awards.slice(0, MAX_OPTIONS_PER_KIND).forEach((award) => {
    const detail = [award.placement, award.event_title].filter(Boolean).join(' · ') || null;
    options.push({
      key: `award-${award.id}`,
      kind: 'award',
      label: award.title,
      caption: detail,
      highlight: { kind: 'award', title: award.title, detail },
      verified: true,
    });
  });

  records
    .filter((r) => r.record_type === 'dyno')
    .slice(0, MAX_OPTIONS_PER_KIND)
    .forEach((r) => {
      const title = r.display_value || r.title || r.record_type_label;
      const detail =
        [r.dyno_type, r.shop_name || r.venue_name, r.date_recorded].filter(Boolean).join(' · ') ||
        null;
      options.push({
        key: `dyno-${r.id}`,
        kind: 'dyno',
        label: title,
        caption: detail,
        highlight: { kind: 'dyno', title, detail },
        verified: r.is_verified,
      });
    });

  records
    .filter((r) => r.is_personal_best && r.record_type !== 'dyno')
    .slice(0, MAX_OPTIONS_PER_KIND)
    .forEach((r) => {
      const title = r.display_value || r.title || r.record_type_label;
      const detail =
        [r.record_type_label, r.venue_name, r.date_recorded].filter(Boolean).join(' · ') || null;
      options.push({
        key: `pb-${r.id}`,
        kind: 'personal_best',
        label: title,
        caption: detail,
        highlight: { kind: 'personal_best', title, detail },
        verified: r.is_verified,
      });
    });

  return options;
}

function ToggleRow({
  id,
  checked,
  onChange,
  label,
  caption,
  disabled = false,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  caption?: string | null;
  disabled?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 min-h-[44px] cursor-pointer transition-colors ${
        disabled
          ? 'border-white/[0.04] bg-[#0c0c0c]/30 opacity-50 cursor-not-allowed'
          : checked
            ? 'border-orange-500/40 bg-orange-500/10'
            : 'border-white/[0.08] bg-[#0c0c0c]/50 hover:border-white/[0.18]'
      }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-white/20 bg-transparent text-orange-500 focus:ring-orange-500/50"
      />
      <span className="min-w-0">
        <span className="block text-sm text-white">{label}</span>
        {caption ? <span className="block text-xs text-gray-500 mt-0.5">{caption}</span> : null}
      </span>
    </label>
  );
}

const ShareCardGenerator: React.FC<ShareCardGeneratorProps> = ({
  isOpen,
  onClose,
  vehicle,
  shareUrl,
  vehicleId,
  ownerName,
  imageUrl,
  qrImageUrl,
  stats = [],
  achievements = [],
  records = [],
  awards = [],
  investedAmount = null,
}) => {
  const { showToast } = useToast();

  const [format, setFormat] = useState<ShareCardFormat>('story');
  const [selectedStatKeys, setSelectedStatKeys] = useState<string[]>([]);
  const [showPhoto, setShowPhoto] = useState(true);
  const [showQr, setShowQr] = useState(true);
  const [showOwner, setShowOwner] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const [showInvested, setShowInvested] = useState(false);
  const [selectedFeatureKeys, setSelectedFeatureKeys] = useState<string[]>([]);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [optionsSeeded, setOptionsSeeded] = useState(false);

  const vehicleImgRef = useRef<HTMLImageElement | null>(null);
  const qrImgRef = useRef<HTMLImageElement | null>(null);
  const brandMarkRef = useRef<HTMLImageElement | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const baseTitle = [vehicle.year, vehicle.make, vehicle.model]
    .filter((part) => part !== null && part !== undefined && `${part}`.trim() !== '')
    .join(' ');
  const cardTitle = baseTitle || vehicle.nickname || 'Bilt build';
  const cardSubtitle = baseTitle && vehicle.nickname ? `"${vehicle.nickname}"` : null;
  const shortUrl = shareUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

  const availableStats = useMemo(
    () =>
      stats.filter(
        (s) =>
          s.value != null &&
          String(s.value).trim() !== '' &&
          !EXCLUDED_STAT_KEYS.has(s.key)
      ),
    [stats]
  );

  const featureOptions = useMemo(
    () => buildFeatureOptions(achievements, awards, records),
    [achievements, awards, records]
  );

  const achievementOptions = featureOptions.filter((o) => o.kind === 'achievement');
  const awardOptions = featureOptions.filter((o) => o.kind === 'award');
  const performanceOptions = featureOptions.filter(
    (o) => o.kind === 'dyno' || o.kind === 'personal_best'
  );

  const investedValue =
    investedAmount != null && investedAmount > 0 ? formatCurrency(investedAmount) : null;

  // Seed defaults once per open so user toggles are not wiped by re-renders.
  useEffect(() => {
    if (!isOpen) {
      setOptionsSeeded(false);
      return;
    }
    if (optionsSeeded) return;

    const defaults = DEFAULT_STAT_KEYS.filter((key) =>
      availableStats.some((s) => s.key === key)
    );
    setSelectedStatKeys(
      defaults.length > 0 ? [...defaults] : availableStats.slice(0, 3).map((s) => s.key)
    );
    setShowPhoto(Boolean(imageUrl));
    setShowQr(Boolean(qrImageUrl));
    setShowOwner(false);
    setShowDate(false);
    setShowInvested(false);
    setSelectedFeatureKeys([]);
    setOptionsSeeded(true);
  }, [isOpen, optionsSeeded, availableStats, imageUrl, qrImageUrl]);

  const canNativeShare = useMemo(() => {
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.share !== 'function' ||
      typeof navigator.canShare !== 'function'
    ) {
      return false;
    }
    try {
      return navigator.canShare({
        files: [new File([new Blob()], 'share.png', { type: 'image/png' })],
      });
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setAssetsLoaded(false);
    Promise.all([
      imageUrl ? loadImageSafe(imageUrl) : Promise.resolve(null),
      qrImageUrl ? loadImageSafe(qrImageUrl) : Promise.resolve(null),
      loadImageSafe(BILT_MARK_MONO_SRC),
    ]).then(([vehicleImg, qrImg, brandMark]) => {
      if (cancelled) return;
      vehicleImgRef.current = vehicleImg;
      qrImgRef.current = qrImg;
      brandMarkRef.current = brandMark;
      setAssetsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, imageUrl, qrImageUrl]);

  const selectedStats = useMemo(
    () =>
      selectedStatKeys
        .map((key) => availableStats.find((s) => s.key === key))
        .filter((s): s is BuildStat => Boolean(s))
        .slice(0, 4),
    [selectedStatKeys, availableStats]
  );

  const selectedFeatures = useMemo(
    () =>
      selectedFeatureKeys
        .map((key) => featureOptions.find((o) => o.key === key))
        .filter((o): o is FeatureOption => Boolean(o))
        .slice(0, MAX_FEATURED),
    [selectedFeatureKeys, featureOptions]
  );

  const toggleStat = useCallback((key: string, next: boolean) => {
    setSelectedStatKeys((prev) => {
      if (next) {
        if (prev.includes(key) || prev.length >= 4) return prev;
        return [...prev, key];
      }
      return prev.filter((k) => k !== key);
    });
  }, []);

  const toggleFeature = useCallback((key: string, next: boolean) => {
    setSelectedFeatureKeys((prev) => {
      if (next) {
        if (prev.includes(key) || prev.length >= MAX_FEATURED) return prev;
        return [...prev, key];
      }
      return prev.filter((k) => k !== key);
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !assetsLoaded || !optionsSeeded) return undefined;
    let cancelled = false;
    setRendering(true);
    setRenderFailed(false);

    const verifiedLabel = selectedFeatures.some((f) => f.verified)
      ? 'Verified result'
      : selectedStats.some((s) => s.verified)
        ? 'Verified specs'
        : null;

    renderShareCard({
      format,
      title: cardTitle,
      subtitle: cardSubtitle,
      ownerName: showOwner ? ownerName || null : null,
      stats: selectedStats.map((s) => ({ label: s.label, value: s.value })),
      highlights: selectedFeatures.map((f) => f.highlight),
      investedText: showInvested && investedValue ? investedValue : null,
      image: showPhoto ? vehicleImgRef.current : null,
      shortUrl,
      qrImage: showQr ? qrImgRef.current : null,
      brandMark: brandMarkRef.current,
      dateText: showDate
        ? new Date().toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : null,
      verifiedLabel,
    })
      .then((blob) => {
        if (cancelled) return;
        blobRef.current = blob;
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setRenderFailed(true);
        showToast('Could not render the share card. Please try again.', 'error', {
          dedupeKey: 'share-card-render-failed',
        });
      })
      .finally(() => {
        if (!cancelled) setRendering(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    assetsLoaded,
    optionsSeeded,
    format,
    cardTitle,
    cardSubtitle,
    ownerName,
    shortUrl,
    showPhoto,
    showQr,
    showOwner,
    showDate,
    showInvested,
    investedValue,
    selectedStats,
    selectedFeatures,
    showToast,
  ]);

  useEffect(() => {
    if (isOpen) return undefined;
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      blobRef.current = null;
      setPreviewUrl(null);
    };
  }, [isOpen]);

  const fileName = useMemo(() => {
    const slug = cardTitle.replace(/\s+/g, '-').toLowerCase().slice(0, 40);
    return `bilt-share-${format}-${slug}.png`;
  }, [cardTitle, format]);

  const trackGenerated = useCallback(
    (action: 'download' | 'native_share') => {
      void apiService.trackEvent('share_card_generated', {
        vehicle_id: vehicleId,
        metadata: {
          format,
          stats: selectedStatKeys,
          features: selectedFeatureKeys,
          show_photo: showPhoto,
          show_qr: showQr,
          show_owner: showOwner,
          show_date: showDate,
          show_invested: showInvested,
          action,
        },
      });
    },
    [
      vehicleId,
      format,
      selectedStatKeys,
      selectedFeatureKeys,
      showPhoto,
      showQr,
      showOwner,
      showDate,
      showInvested,
    ]
  );

  const handleDownload = useCallback(() => {
    const blob = blobRef.current;
    if (!blob) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
    trackGenerated('download');
    showToast('Share card downloaded.', 'success');
  }, [fileName, trackGenerated, showToast]);

  const handleNativeShare = useCallback(async () => {
    const blob = blobRef.current;
    if (!blob) return;
    try {
      const file = new File([blob], fileName, { type: 'image/png' });
      await navigator.share({ files: [file], title: cardTitle, url: shareUrl });
      trackGenerated('native_share');
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      showToast('Sharing failed. You can download the card instead.', 'error');
    }
  }, [fileName, cardTitle, shareUrl, trackGenerated, showToast]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      showToast('Link copied.', 'success', { dedupeKey: 'share-card-link-copied' });
    } catch {
      showToast('Could not copy the link.', 'error');
    }
  }, [shareUrl, showToast]);

  const formatLabel = FORMAT_CHOICES.find((f) => f.value === format)?.label ?? format;
  const busy = rendering || !assetsLoaded || !optionsSeeded;
  const featureSlotsLeft = MAX_FEATURED - selectedFeatureKeys.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create share card" size="xl">
      <div className="grid gap-6 md:grid-cols-[320px,minmax(0,1fr)]">
        <div className="max-h-[70vh] overflow-y-auto overscroll-contain pr-1 space-y-6">
          <fieldset>
            <legend className="text-sm font-medium text-gray-300 mb-2">Format</legend>
            <div className="grid grid-cols-3 gap-2">
              {FORMAT_CHOICES.map((choice) => (
                <label key={choice.value} className="cursor-pointer">
                  <input
                    type="radio"
                    name="share-card-format"
                    value={choice.value}
                    checked={format === choice.value}
                    onChange={() => setFormat(choice.value)}
                    className="sr-only peer"
                  />
                  <span className="flex flex-col items-center gap-1.5 rounded-lg border border-white/[0.08] bg-[#0c0c0c]/50 px-2 py-3 transition-colors hover:border-white/[0.18] peer-checked:border-orange-500 peer-checked:bg-orange-500/10 peer-focus-visible:ring-2 peer-focus-visible:ring-orange-500">
                    <span
                      aria-hidden
                      className={`rounded-sm bg-[#1a1a1a] ${choice.previewClass}`}
                    />
                    <span className="text-sm font-medium text-white">{choice.label}</span>
                    <span className="text-xs text-gray-500">{choice.caption}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {availableStats.length > 0 ? (
            <fieldset>
              <legend className="text-sm font-medium text-gray-300 mb-1">Stats</legend>
              <p className="text-xs text-gray-500 mb-2">
                Power, torque, and modifications are on by default. Up to 4.
              </p>
              <div className="space-y-2">
                {availableStats.map((stat) => {
                  const checked = selectedStatKeys.includes(stat.key);
                  const atLimit = !checked && selectedStatKeys.length >= 4;
                  return (
                    <ToggleRow
                      key={stat.key}
                      id={`share-stat-${stat.key}`}
                      checked={checked}
                      disabled={atLimit}
                      onChange={(next) => toggleStat(stat.key, next)}
                      label={stat.label}
                      caption={`${stat.value}${stat.detail ? ` · ${stat.detail}` : ''}`}
                    />
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          <fieldset>
            <legend className="text-sm font-medium text-gray-300 mb-2">Include</legend>
            <div className="space-y-2">
              <ToggleRow
                id="share-include-photo"
                checked={showPhoto}
                onChange={setShowPhoto}
                label="Vehicle photo"
                caption={imageUrl ? null : 'No photo available'}
                disabled={!imageUrl}
              />
              <ToggleRow
                id="share-include-qr"
                checked={showQr}
                onChange={setShowQr}
                label="QR code"
                caption={qrImageUrl ? null : 'Generate a QR code first'}
                disabled={!qrImageUrl}
              />
              <ToggleRow
                id="share-include-owner"
                checked={showOwner}
                onChange={setShowOwner}
                label="Builder name"
                caption={ownerName || 'No builder name'}
                disabled={!ownerName}
              />
              <ToggleRow
                id="share-include-date"
                checked={showDate}
                onChange={setShowDate}
                label="Today’s date"
              />
              <ToggleRow
                id="share-include-invested"
                checked={showInvested}
                onChange={setShowInvested}
                label="Invested total"
                caption={investedValue ? investedValue : 'No priced mods yet'}
                disabled={!investedValue}
              />
            </div>
          </fieldset>

          {(achievementOptions.length > 0 ||
            awardOptions.length > 0 ||
            performanceOptions.length > 0) && (
            <fieldset>
              <legend className="text-sm font-medium text-gray-300 mb-1">
                Recognition
              </legend>
              <p className="text-xs text-gray-500 mb-2">
                Pick achievements, awards, or performance results. Up to {MAX_FEATURED}.
                {featureSlotsLeft === 0 ? ' Limit reached.' : ''}
              </p>

              {achievementOptions.length > 0 ? (
                <div className="mb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                    Achievements
                  </p>
                  <div className="space-y-2">
                    {achievementOptions.map((option) => {
                      const checked = selectedFeatureKeys.includes(option.key);
                      const atLimit = !checked && featureSlotsLeft <= 0;
                      return (
                        <ToggleRow
                          key={option.key}
                          id={`share-feature-${option.key}`}
                          checked={checked}
                          disabled={atLimit}
                          onChange={(next) => toggleFeature(option.key, next)}
                          label={option.label}
                          caption={option.caption}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {awardOptions.length > 0 ? (
                <div className="mb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                    Awards
                  </p>
                  <div className="space-y-2">
                    {awardOptions.map((option) => {
                      const checked = selectedFeatureKeys.includes(option.key);
                      const atLimit = !checked && featureSlotsLeft <= 0;
                      return (
                        <ToggleRow
                          key={option.key}
                          id={`share-feature-${option.key}`}
                          checked={checked}
                          disabled={atLimit}
                          onChange={(next) => toggleFeature(option.key, next)}
                          label={option.label}
                          caption={option.caption}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {performanceOptions.length > 0 ? (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                    Performance
                  </p>
                  <div className="space-y-2">
                    {performanceOptions.map((option) => {
                      const checked = selectedFeatureKeys.includes(option.key);
                      const atLimit = !checked && featureSlotsLeft <= 0;
                      return (
                        <ToggleRow
                          key={option.key}
                          id={`share-feature-${option.key}`}
                          checked={checked}
                          disabled={atLimit}
                          onChange={(next) => toggleFeature(option.key, next)}
                          label={option.label}
                          caption={option.caption}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </fieldset>
          )}

          <p className="text-xs text-gray-500">
            Only public build-page data can appear on the card.
          </p>
        </div>

        <div className="flex flex-col">
          <div
            className="flex items-center justify-center rounded-xl border border-white/[0.06] bg-[#0a0a0a]/60 p-4 min-h-[300px]"
            aria-live="polite"
          >
            {busy ? (
              <div role="status" className="flex flex-col items-center gap-3 text-gray-400">
                <span className="spinner-sm" aria-hidden />
                <span className="text-sm">Rendering preview…</span>
              </div>
            ) : renderFailed ? (
              <p className="text-sm text-red-300 text-center px-4">
                Preview failed to render. Adjust the options or try again.
              </p>
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt={`Share card preview for ${cardTitle} in ${formatLabel} format`}
                className="max-h-[340px] w-auto rounded-lg shadow-lg shadow-black/40"
              />
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              variant="primary"
              onClick={handleDownload}
              disabled={busy || renderFailed || !previewUrl}
              loading={busy}
              loadingLabel="Rendering…"
            >
              Download PNG
            </Button>
            {canNativeShare && (
              <Button
                variant="secondary"
                onClick={handleNativeShare}
                disabled={busy || renderFailed || !previewUrl}
              >
                Share
              </Button>
            )}
            <Button variant="secondary" onClick={handleCopyLink}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShareCardGenerator;
